import { useTheme } from '@emotion/react'
import {
    Product,
    Profile,
    RequiredAccess,
    requiredAccessValue,
    useFeature,
} from '@news-mono/web-common'
import React from 'react'
import { CommentButton } from '../../..'
import { FundingType } from '../../../templates/Publication/lib/get-funding-type'
import {
    TimestampFormat,
    TimestampLabel,
} from '../../../typography/Timestamp/Timestamp'
import { themedValue } from '../../../__styling/themed-value'
import {
    StyledBylineText,
    StyledBylineTextWrapper,
    StyledMetaDataWrapper,
    StyledPremiumIcon,
    StyledPremiumWrapper,
} from '../Byline.styled'
import { BylineContent } from './BylineContent'
import { BylineTimestamp } from './BylineTimestamp'
import { BylineTimestampUpdated } from './BylineTimestampUpdated'

interface BylineTextProps {
    /** @deprecated Condenses `BylineText` & `BylineWrapper` onto one line -- only used on Seven non-bauhaus themed pages */
    inlineTimestamp?: boolean
    profile: Profile[] | undefined
    fundingType?: FundingType
    source: string | undefined
    timestamp?: string | undefined
    timestampFormat?: TimestampFormat
    timestampUpdated?: string | undefined
    timestampUpdatedLabel?: TimestampLabel
    timestampUpdatedFormat?: TimestampFormat
    text: string | undefined
    requiredAccess?: RequiredAccess
    allowCommenting: boolean
}

export const BylineText: React.FC<BylineTextProps> = ({
    inlineTimestamp = false,
    profile,
    fundingType,
    source,
    timestamp,
    timestampFormat,
    timestampUpdated,
    timestampUpdatedFormat,
    timestampUpdatedLabel,
    text,
    requiredAccess,
    allowCommenting,
}) => {
    const theme = useTheme()

    /* Published Timestamp Format */
    const publishedFormat = themedValue(theme, {
        thewest: 'dateTimeShortDay',
        sevennews: timestampUpdated
            ? 'dayMonthYear'
            : 'customSevenNewsDateTime',
        perthnow: 'default',
    }) as TimestampFormat

    /* Updated Timestamp Format */
    const updatedFormat = themedValue(theme, {
        thewest: timestampUpdatedFormat || 'dateTimeOnly',
        sevennews: 'customSevenNewsDateTime',
        fallback: 'dateTimeOnly',
    }) as TimestampFormat

    const isRestricted = requiredAccess && requiredAccess.level !== 'anonymous'
    const commentToggle = useFeature('comments')

    const showCommentButton =
        allowCommenting && theme.kind === Product.TheWest && commentToggle

    /**                   ↓----Byline Text----↓
     *  |~~~~~~~~~~~|~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~|~~~~~~~~~~~~~~~|
     *  | profile   |   Top Row // Byline Content   | Button        |
     *  | pic       |~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~| Container     |
     *  |           |   Bottom Row // MetaData      |               |
     *  |___________|_______________________________|_______________|
     */

    /**
     * Seven Version with both Top and Bottom Row condensed into one line.
     * No Longer used with Bauhaus and can be removed once the theme migration is
     * complete.
     */

    if (inlineTimestamp) {
        return (
            <StyledBylineText>
                <StyledBylineTextWrapper>
                    <BylineContent
                        profiles={profile}
                        inlineTimestamp={inlineTimestamp}
                        fundingType={fundingType}
                        source={source}
                        timestamp={timestamp}
                        timestampUpdated={timestampUpdated}
                        timestampFormat={publishedFormat}
                        timestampUpdatedFormat={updatedFormat}
                        text={text}
                        timestampUpdatedLabel={timestampUpdatedLabel}
                    />
                </StyledBylineTextWrapper>
            </StyledBylineText>
        )
    }

    return (
        <StyledBylineText>
            {/* Top Row of Byline Content*/}
            <StyledBylineTextWrapper>
                {/* TODO This has complicated props as a result of the old inlineTimestamp,
                pre-bauhaus styling and should bre removed/refactored when possible */}
                <BylineContent
                    profiles={profile}
                    inlineTimestamp={inlineTimestamp}
                    fundingType={fundingType}
                    source={source}
                    timestamp={timestamp}
                    timestampUpdated={timestampUpdated}
                    timestampFormat={publishedFormat}
                    timestampUpdatedFormat={updatedFormat}
                    text={text}
                />
            </StyledBylineTextWrapper>

            {/* Bottom Row of Byline Content */}
            {(timestamp || timestampUpdated || isRestricted) && (
                <StyledMetaDataWrapper>
                    {/* Timestamps */}
                    {timestamp && (
                        <BylineTimestamp
                            timestamp={timestamp}
                            timestampFormat={publishedFormat}
                        />
                    )}
                    {timestampUpdated && (
                        <BylineTimestampUpdated
                            timestamp={timestampUpdated}
                            timestampLabel={timestampUpdatedLabel}
                            timestampUpdatedFormat={updatedFormat}
                        />
                    )}
                    {/* Inline Comment Button / Link */}
                    {showCommentButton && <CommentButton />}
                    {/* Subscriber Premium Icon */}
                    {requiredAccessValue(requiredAccess, {
                        subscriber: (
                            <StyledPremiumWrapper>
                                <StyledPremiumIcon width={17} height={17} />
                            </StyledPremiumWrapper>
                        ),
                        anonymous: undefined,
                    })}
                </StyledMetaDataWrapper>
            )}
        </StyledBylineText>
    )
}
